// Rekap Tanya ETAMS — layar Owner (K200 butir 13–14, 27 Sep 2026).
//
// Pemakaian bulan ini terhadap batas Rp500.000, jumlah pertanyaan per hasil, dan pertanyaan yang TIDAK terjawab
// dikelompokkan per kata kunci supaya Owner bisa menjadikannya usulan isi Panduan atau pertanyaan Owner.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import Hint from "../components/etams/Hint";

const LABEL_HASIL = {
  DIJAWAB: "Terjawab",
  TIDAK_TAHU: "Tidak terjawab",
  BATAS_BIAYA: "Kena batas",
  BELUM_AKTIF: "Belum aktif",
  GAGAL: "Gagal",
};
const LABEL_TINDAK = {
  USULAN_PANDUAN: "Jadi usulan Panduan",
  PERTANYAAN_OWNER: "Jadi pertanyaan Owner",
  SELESAI: "Selesai",
};

function bulanIni() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function TanyaEtamsRekap() {
  const [bulan, setBulan] = useState(bulanIni());
  const [data, setData] = useState(null);
  const [galat, setGalat] = useState(null);
  const [sibuk, setSibuk] = useState(false);

  const muat = useCallback(() => {
    setGalat(null);
    api.get("/tanya-etams/rekap", { params: { bulan } })
      .then((r) => setData(r.data))
      .catch((e) => setGalat(apiErrorMessage(e, "Rekap belum bisa dimuat.")));
  }, [bulan]);
  useEffect(() => { muat(); }, [muat]);

  const tandai = async (ids, tindak) => {
    setSibuk(true);
    try {
      for (const id of ids) await api.post(`/tanya-etams/${id}/tindak-lanjut`, { tindak_lanjut: tindak });
      toast.success(`${ids.length} pertanyaan ditandai: ${LABEL_TINDAK[tindak]}.`);
      muat();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menandai pertanyaan."));
    } finally {
      setSibuk(false);
    }
  };

  const persen = data ? Math.min(100, Math.round((data.biaya_rp / data.batas_rp) * 100)) : 0;
  return (
    <div className="max-w-5xl space-y-4" data-testid="halaman-rekap-tanya-etams">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-xl font-semibold">Rekap Tanya ETAMS</h1>
        <label className="text-sm">
          Bulan{" "}
          <input type="month" value={bulan} onChange={(e) => setBulan(e.target.value)}
            className="ml-1 rounded-md border bg-background px-2 py-1" data-testid="rekap-bulan" />
        </label>
      </div>
      {galat && <p className="text-sm text-danger">{galat}</p>}
      {!data && !galat && <p className="text-sm text-muted-foreground">Memuat…</p>}

      {data && (
        <>
          <Card className="space-y-2 p-4">
            <p className="flex items-center gap-1 text-sm font-medium">
              Pemakaian bulan ini
              <Hint>Batas Rp500.000 per bulan untuk seluruh ETAMSIOS. Di 80% Owner diberi tahu; di 100% Tanya ETAMS
                berhenti sampai awal bulan berikutnya.</Hint>
            </p>
            <Progress value={persen} className="h-2" />
            <p className="text-sm" data-testid="rekap-biaya">
              Rp{Math.round(data.biaya_rp).toLocaleString("id-ID")} dari Rp{data.batas_rp.toLocaleString("id-ID")} ({persen}%)
              · {data.jumlah} pertanyaan
            </p>
            <div className="flex flex-wrap gap-2 text-xs">
              {Object.entries(data.per_hasil || {}).map(([k, n]) => (
                <span key={k} className="rounded-full bg-neutral-soft px-2 py-0.5 text-neutral-soft-foreground">
                  {LABEL_HASIL[k] || k}: {n}
                </span>
              ))}
            </div>
          </Card>

          <Card className="space-y-3 p-4">
            <p className="flex items-center gap-1 text-sm font-medium">
              Pertanyaan yang belum terjawab
              <Hint>Dikelompokkan menurut kata yang paling sering muncul. Tandai tiap kelompok supaya jadi bahan isi
                Panduan atau pertanyaan untuk Owner.</Hint>
            </p>
            {(data.kelompok_belum_terjawab || []).length === 0 && (
              <p className="text-sm text-muted-foreground">Belum ada pertanyaan yang tidak terjawab bulan ini.</p>
            )}
            {(data.kelompok_belum_terjawab || []).map((g) => (
              <div key={g.kata_kunci} className="rounded-md border p-3" data-testid="rekap-kelompok">
                <p className="text-sm font-medium">"{g.kata_kunci}" · {g.jumlah} pertanyaan</p>
                <ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground">
                  {g.contoh.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(LABEL_TINDAK).map(([k, l]) => (
                    <Button key={k} size="sm" variant="outline" disabled={sibuk} onClick={() => tandai(g.ids, k)}>{l}</Button>
                  ))}
                </div>
              </div>
            ))}
          </Card>

          <Card className="p-4">
            <p className="mb-2 text-sm font-medium">Pertanyaan terbaru</p>
            <div className="divide-y">
              {(data.terbaru || []).map((r) => (
                <div key={r.id} className="py-2 text-sm" data-testid="rekap-baris">
                  <p className="font-medium">{r.pertanyaan}</p>
                  <p className="text-muted-foreground">
                    {LABEL_HASIL[r.hasil] || r.hasil} · {new Date(r.created_at).toLocaleString("id-ID")}
                    {r.tindak_lanjut ? ` · ${LABEL_TINDAK[r.tindak_lanjut]}` : ""}
                  </p>
                  {r.jawaban && <p className="mt-1 whitespace-pre-line">{r.jawaban}</p>}
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
